/** Preset services and hours for the 9 professions. Editing happens later under "אני". */
import type { Profession, TimeRange } from '../domain/types';

export interface ServicePreset {
  name: string;
  category: string;
  durationMin: number;
  bufferMin: number;
  priceNis: number;
  depositNis?: number;
}
export interface ProfessionPreset {
  profession: Profession;
  labelHe: string;
  labelEn: string;
  services: ServicePreset[];
}

/** Sun-Thu 09:00-18:00, Fri 09:00-13:00, Sat off. Index = weekday (0 = Sunday). */
export const DEFAULT_WEEKLY_RANGES: TimeRange[][] = [
  [{ start: '09:00', end: '18:00' }], [{ start: '09:00', end: '18:00' }], [{ start: '09:00', end: '18:00' }],
  [{ start: '09:00', end: '18:00' }], [{ start: '09:00', end: '18:00' }], [{ start: '09:00', end: '13:00' }], [],
];

export const PROFESSION_PRESETS: Record<Profession, ProfessionPreset> = {
  nails: {
    profession: 'nails', labelHe: 'ציפורניים', labelEn: 'Nails',
    services: [
      { name: 'לק ג׳ל', category: 'ידיים', durationMin: 60, bufferMin: 10, priceNis: 150 },
      { name: 'מניקור בסיסי', category: 'ידיים', durationMin: 45, bufferMin: 10, priceNis: 90 },
      { name: 'בניית ציפורניים בג׳ל', category: 'בנייה', durationMin: 120, bufferMin: 15, priceNis: 280 },
      { name: 'מילוי בג׳ל', category: 'בנייה', durationMin: 90, bufferMin: 15, priceNis: 200, depositNis: 40 },
      { name: 'פדיקור ג׳ל', category: 'רגליים', durationMin: 60, bufferMin: 10, priceNis: 170 },
      { name: 'הסרת ג׳ל', category: 'ידיים', durationMin: 30, bufferMin: 5, priceNis: 60 },
    ],
  },
  lashes: {
    profession: 'lashes', labelHe: 'ריסים', labelEn: 'Lashes',
    services: [
      { name: 'הרמת ריסים', category: 'טיפולים', durationMin: 60, bufferMin: 10, priceNis: 220, depositNis: 50 },
      { name: 'הארכת ריסים קלאסית', category: 'הארכות', durationMin: 120, bufferMin: 15, priceNis: 350, depositNis: 70 },
      { name: 'הארכת ריסים וולום', category: 'הארכות', durationMin: 150, bufferMin: 15, priceNis: 420, depositNis: 80 },
      { name: 'מילוי ריסים', category: 'הארכות', durationMin: 75, bufferMin: 10, priceNis: 200, depositNis: 40 },
      { name: 'הסרת ריסים', category: 'הארכות', durationMin: 30, bufferMin: 5, priceNis: 60 },
    ],
  },
  brows: {
    profession: 'brows', labelHe: 'גבות', labelEn: 'Brows',
    services: [
      { name: 'עיצוב גבות', category: 'גבות', durationMin: 30, bufferMin: 5, priceNis: 80 },
      { name: 'עיצוב וצבע גבות', category: 'גבות', durationMin: 45, bufferMin: 5, priceNis: 120 },
      { name: 'הנקה (חינה) לגבות', category: 'גבות', durationMin: 45, bufferMin: 5, priceNis: 130 },
      { name: 'הרמת גבות', category: 'גבות', durationMin: 60, bufferMin: 10, priceNis: 240, depositNis: 50 },
      { name: 'פיגמנט גבות', category: 'קעקוע קוסמטי', durationMin: 120, bufferMin: 15, priceNis: 900, depositNis: 200 },
    ],
  },
  barber: {
    profession: 'barber', labelHe: 'ספרות', labelEn: 'Barber',
    services: [
      { name: 'תספורת גברים', category: 'שיער', durationMin: 30, bufferMin: 5, priceNis: 70 },
      { name: 'תספורת וזקן', category: 'שיער', durationMin: 45, bufferMin: 5, priceNis: 100 },
      { name: 'עיצוב זקן', category: 'זקן', durationMin: 20, bufferMin: 5, priceNis: 45 },
      { name: 'תספורת ילדים', category: 'שיער', durationMin: 25, bufferMin: 5, priceNis: 55 },
      { name: 'גילוח במגבת חמה', category: 'זקן', durationMin: 30, bufferMin: 5, priceNis: 80 },
    ],
  },
  hair: {
    profession: 'hair', labelHe: 'שיער', labelEn: 'Hair',
    services: [
      { name: 'תספורת נשים', category: 'תספורת', durationMin: 60, bufferMin: 10, priceNis: 180 },
      { name: 'פן', category: 'עיצוב', durationMin: 45, bufferMin: 10, priceNis: 120 },
      { name: 'צבע שורשים', category: 'צבע', durationMin: 120, bufferMin: 15, priceNis: 280, depositNis: 60 },
      { name: 'גוונים', category: 'צבע', durationMin: 180, bufferMin: 15, priceNis: 650, depositNis: 150 },
      { name: 'החלקה', category: 'טיפולים', durationMin: 180, bufferMin: 15, priceNis: 700, depositNis: 150 },
      { name: 'תסרוקת לאירוע', category: 'עיצוב', durationMin: 75, bufferMin: 10, priceNis: 300, depositNis: 80 },
    ],
  },
  cosmetics: {
    profession: 'cosmetics', labelHe: 'קוסמטיקה', labelEn: 'Cosmetics',
    services: [
      { name: 'טיפול פנים קלאסי', category: 'פנים', durationMin: 60, bufferMin: 10, priceNis: 250 },
      { name: 'ניקוי פנים עמוק', category: 'פנים', durationMin: 75, bufferMin: 10, priceNis: 300, depositNis: 60 },
      { name: 'פילינג כימי עדין', category: 'פנים', durationMin: 45, bufferMin: 10, priceNis: 280 },
      { name: 'עיסוי פנים והרפיה', category: 'פנים', durationMin: 45, bufferMin: 5, priceNis: 200 },
    ],
  },
  skincare: {
    profession: 'skincare', labelHe: 'טיפוח עור', labelEn: 'Skincare',
    services: [
      { name: 'אבחון עור וייעוץ', category: 'ייעוץ', durationMin: 30, bufferMin: 5, priceNis: 100 },
      { name: 'טיפול הידרה פייס', category: 'פנים', durationMin: 60, bufferMin: 10, priceNis: 350, depositNis: 70 },
      { name: 'טיפול אקנה', category: 'פנים', durationMin: 60, bufferMin: 10, priceNis: 280 },
      { name: 'מזותרפיה ללא מחטים', category: 'פנים', durationMin: 60, bufferMin: 10, priceNis: 400, depositNis: 80 },
      { name: 'טיפול אנטי אייג׳ינג', category: 'פנים', durationMin: 75, bufferMin: 10, priceNis: 450, depositNis: 90 },
    ],
  },
  hair_removal: {
    profession: 'hair_removal', labelHe: 'הסרת שיער', labelEn: 'Hair removal',
    services: [
      { name: 'הסרת שיער בלייזר: פנים', category: 'לייזר', durationMin: 20, bufferMin: 5, priceNis: 150 },
      { name: 'הסרת שיער בלייזר: בית שחי', category: 'לייזר', durationMin: 15, bufferMin: 5, priceNis: 120 },
      { name: 'הסרת שיער בלייזר: רגליים', category: 'לייזר', durationMin: 45, bufferMin: 10, priceNis: 380, depositNis: 70 },
      { name: 'הסרת שיער בשעווה: רגליים', category: 'שעווה', durationMin: 40, bufferMin: 5, priceNis: 150 },
      { name: 'הסרת שיער בשעווה: ביקיני', category: 'שעווה', durationMin: 30, bufferMin: 5, priceNis: 130 },
      { name: 'הסרת שיער בשעווה: פנים', category: 'שעווה', durationMin: 15, bufferMin: 5, priceNis: 60 },
    ],
  },
  makeup: {
    profession: 'makeup', labelHe: 'איפור', labelEn: 'Makeup',
    services: [
      { name: 'איפור יום', category: 'איפור', durationMin: 45, bufferMin: 10, priceNis: 250 },
      { name: 'איפור ערב', category: 'איפור', durationMin: 60, bufferMin: 10, priceNis: 350, depositNis: 70 },
      { name: 'איפור כלה', category: 'כלות', durationMin: 120, bufferMin: 15, priceNis: 900, depositNis: 300 },
      { name: 'פגישת ניסיון לכלה', category: 'כלות', durationMin: 90, bufferMin: 15, priceNis: 450, depositNis: 100 },
      { name: 'שיעור איפור אישי', category: 'שיעורים', durationMin: 90, bufferMin: 10, priceNis: 400 },
    ],
  },
};

export const PROFESSION_LABELS_HE: Record<Profession, string> = Object.fromEntries(
  Object.values(PROFESSION_PRESETS).map((p) => [p.profession, p.labelHe]),
) as Record<Profession, string>;
